import { useState, useEffect, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  FormField,
  ContentLayout,
  Box,
  Alert,
  Toggle,
  Input,
  Textarea,
  Flashbar,
  Spinner,
  ColumnLayout,
  Cards
} from '@amzn/awsui-components-react';
import { ToolParameterSchema } from 'src/store/interfaces/Tool.types';
import { useGetToolRevisionByIdQuery, useCreateToolRevisionMutation } from 'src/store/apis/toolApi';
import useNotification from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import ToolParametersTable from './ToolParametersTable';
import { downloadToolSchemaAsYaml } from '../../utils/yamlExporter';

const ViewRevision: FC = () => {
  const { toolId, version, revision } = useParams<{ toolId: string; version: string; revision: string }>();
  const navigate = useNavigate();
  const { addNotificationItem, notificationItems } = useNotification();
  const { canEditTools } = usePermissions();
  
  const [isEditable, setIsEditable] = useState(false);
  const [editedParameterName, setEditedParameterName] = useState('');
  const [editedSummary, setEditedSummary] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedParameters, setEditedParameters] = useState<(ToolParameterSchema & { internalName: string })[]>([]);
  
  const { data: revisionData, error: revisionError, isLoading: revisionLoading } = useGetToolRevisionByIdQuery({
    toolId: toolId!,
    version: version!,
    revision: parseInt(revision!)
  });
  const [createToolRevision, { isLoading: isCreating }] = useCreateToolRevisionMutation();
  

  useEffect(() => {
    if (!canEditTools) {
      setIsEditable(false);
    }
  }, [canEditTools]);

  useEffect(() => {
    if (revisionData) {
      setEditedParameterName(revisionData.toolName || '');
      setEditedSummary(revisionData.summary || '');
      setEditedDescription(revisionData.description || '');
      const parametersFromSchema = revisionData.inputSchema ?
        Object.entries(revisionData.inputSchema as Record<string, ToolParameterSchema>).map(([key, param]) => ({
          internalName: key,
          ...param
        })) : [];

      setEditedParameters(parametersFromSchema);
    }
  }, [revisionData]);

  const handleClose = () => {
    navigate(`/tools/${toolId}`, { state: { toolId: toolId } });
  };

  const handleToggleEdit = () => {
    setIsEditable(!isEditable);
    if (isEditable && revisionData) {
      setEditedParameterName(revisionData.toolName || '');
      setEditedSummary(revisionData.summary || '');
      setEditedDescription(revisionData.description || '');

      const parametersFromSchema = revisionData.inputSchema ?
        Object.entries(revisionData.inputSchema as Record<string, ToolParameterSchema>).map(([key, param]) => ({
          internalName: key,
          ...param
        })) : [];

      setEditedParameters(parametersFromSchema);
    }
  };

  const handleSave = async () => {
    if (!toolId || !version) return;
    
    try {
      const inputSchema: Record<string, ToolParameterSchema> = {};
      editedParameters.forEach((param) => {
        const paramKey = param.internalName;
        inputSchema[paramKey]= {
          type: param.type,
          enum: param.enum && param.enum.length > 0 ? param.enum : undefined,
          default: param.default,
          required: !!param.required,
          description: param.description || '',
          examples: param.examples,
          parameterName: param.parameterName || '',
        };
        
        if (param.items) {
          inputSchema[paramKey].items = param.items;
        }

      });
      
      const newRevisionData = {
        toolId: toolId,
        version: version,
        revision: revisionData ? revisionData.revision + 1 : 1,
        toolName: editedParameterName,
        summary: editedSummary || undefined,
        description: editedDescription || '',
        inputSchema: inputSchema
      };
      await createToolRevision({ toolId, version, data: newRevisionData }).unwrap();
      
      addNotificationItem({
        type: 'success',
        content: `Successfully created a new revision for ${toolId}`,
        id: 'revision-created'
      });
      
      setIsEditable(false);
      
      navigate(`/tools/${toolId}`, { state: { toolId: toolId } });
      
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = (error as Error).message;
      addNotificationItem({
        type: 'error',
        content: `Error creating new revision: ${errorMessage}`,
        id: 'revision-error'
      });
    }
  };

  const handleParameterChange = (index: number, field: keyof (ToolParameterSchema & { internalName: string }), value: any) => {
    const updatedParameters = [...editedParameters];
    updatedParameters[index] = {
      ...updatedParameters[index],
      [field]: value
    };
    setEditedParameters(updatedParameters);
  };

  const handleDownloadYaml = () => {
    if (revisionData && toolId) {
      downloadToolSchemaAsYaml(revisionData, toolId, addNotificationItem);
    }
  };

  const parametersForTable = isEditable ? editedParameters : (revisionData?.inputSchema ? 
    Object.entries(revisionData.inputSchema as Record<string, ToolParameterSchema >).map(([key, param]) => ({
      internalName: key,
      ...param
    })) : []);


  if (!revisionData) {
    return (
      <ContentLayout>
        <Container>
          <Box textAlign="center" padding="xxl">
            <Spinner size="large" />
            <Box variant="p" padding={{ top: "s" }}>
              Loading revision data...
            </Box>
          </Box>
        </Container>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`View revision ${revisionData.revision} for ${revisionData.toolName}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {canEditTools && (
                <Toggle
                  onChange={({ detail }) => handleToggleEdit()}
                  checked={isEditable}
                >
                  Edit Mode
                </Toggle>
              )}
              {!isEditable && (
                <Button 
                  onClick={handleDownloadYaml}
                  iconName="download"
                >
                  Download YAML
                </Button>
              )}
              {isEditable && canEditTools && (
                <>
                  <Button onClick={handleToggleEdit}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    loading={isCreating}
                    onClick={handleSave}
                  >
                    Create New Revision
                  </Button>
                </>
              )}
              {!isEditable && (
                <Button variant="primary" onClick={handleClose}>
                  Close
                </Button>
              )}
            </SpaceBetween>
          }
        >
          View Tool Revision
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} stackItems />
        {!canEditTools && (
          <Alert type="info" statusIconAriaLabel="Info">
            You have view-only access to this page. Sufficient permission is required to make changes.
          </Alert>
        )}
        {isEditable && (
          <Alert type="info">
            <strong>Note:</strong> Making changes will create a new revision instead of modifying this existing revision. The current revision will remain unchanged.
          </Alert>
        )}
        <SpaceBetween size="l">
          {/* Tool Metadata Section */}
          <Container
            header={
              <Header variant="h2">
                Tool Metadata
              </Header>
            }
          >
            <ColumnLayout columns={3} variant="text-grid">
              <FormField label="Tool ID">
                <Box variant="code" fontSize="body-m" padding={{ vertical: "xs", horizontal: "s" }}>
                  {toolId}
                </Box>
              </FormField>
              <FormField label="Version">
                <Box variant="code" fontSize="body-m" padding={{ vertical: "xs", horizontal: "s" }}>
                  {revisionData.version}
                </Box>
              </FormField>
              <FormField label="Revision">
                <Box variant="code" fontSize="body-m" padding={{ vertical: "xs", horizontal: "s" }}>
                  {revisionData.revision}
                </Box>
              </FormField>
            </ColumnLayout>
          </Container>

          {/* Tool Configuration Section */}
          <Container
            header={
              <Header variant="h2">
                Tool Description
              </Header>
            }
          >
            <SpaceBetween size="l">
              <FormField 
                label="Tool Name"
                constraintText={isEditable ? "This field is editable" : "Enable edit mode to modify"}
              >
                <Input
                  value={editedParameterName}
                  onChange={({ detail }) => setEditedParameterName(detail.value)}
                  placeholder="Enter tool name"
                  readOnly={!isEditable}
                />
              </FormField>
              
              <FormField
                label="Summary (Progressive Exposure)"
                description="Short 2-3 sentence summary shown in MCP tools/list. When set, the full description is only served via getToolParameters."
                constraintText={isEditable ? "This field is editable" : "Enable edit mode to modify"}
              >
                <Textarea
                  value={editedSummary}
                  onChange={({ detail }) => setEditedSummary(detail.value)}
                  rows={3}
                  placeholder="Short summary for tool selection (leave empty to show full description)"
                  readOnly={!isEditable}
                />
              </FormField>

              <FormField
                label="Tool Description"
                constraintText={isEditable ? "This field is editable" : "Enable edit mode to modify"}
              >
                <Textarea
                  value={editedDescription}
                  onChange={({ detail }) => setEditedDescription(detail.value)}
                  rows={10}
                  placeholder="Enter tool description"
                  readOnly={!isEditable}
                />
              </FormField>

            </SpaceBetween>
          </Container>
        </SpaceBetween>

        <Container>
          <ToolParametersTable
            parameters={parametersForTable}
            onParameterChange={handleParameterChange}
            requiredParameters={revisionData?.required || []}
            isEditable={isEditable}
          />
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default ViewRevision;
